import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { aiAgentsApi } from "../../lib/api";
import { Loader2, Save, Clock } from "lucide-react";

function Toggle({ checked, onChange, testId, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      data-testid={testId}
      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${checked ? "bg-indigo-600" : "bg-slate-300"}`}
    >
      <span
        className="inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform"
        style={{ transform: checked ? "translateX(18px)" : "translateX(2px)" }}
      />
    </button>
  );
}

export default function CallSummarySettings({ isAdmin }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);

  const load = async () => {
    try {
      setSettings(await aiAgentsApi.callSummarySettings());
    } catch (e) {
      toast.error("Failed to load call summary settings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const data = await aiAgentsApi.saveCallSummarySettings(settings);
      setSettings(data);
      toast.success("Call summary settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !settings) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="call-summary-settings-loading">
        <Loader2 className="h-7 w-7 text-indigo-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl" data-testid="call-summary-settings-panel">
      <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-5">
        <div>
          <label className="block text-[13px] font-semibold text-slate-800 mb-1.5">Send call summaries to</label>
          <input
            type="email"
            value={settings.email}
            onChange={(e) => setSettings((s) => ({ ...s, email: e.target.value }))}
            placeholder="you@company.com"
            data-testid="call-summary-email-input"
            disabled={!isAdmin}
            className="w-full h-10 px-3 rounded-lg border border-slate-300 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-indigo-500/30 disabled:bg-slate-50"
          />
        </div>

        <div className="flex items-start justify-between gap-4 pt-4 border-t border-slate-100" data-testid="call-summary-per-call-row">
          <div>
            <div className="text-[13.5px] font-semibold text-slate-800">Email me a summary after each inbound call</div>
            <div className="text-[12px] text-slate-500 mt-0.5">Sent immediately when an inbound call completes — date, time, phone, name, transcript snippet, and outcome.</div>
          </div>
          <Toggle
            checked={settings.perCallEnabled}
            onChange={(v) => setSettings((s) => ({ ...s, perCallEnabled: v }))}
            testId="call-summary-per-call-toggle"
            disabled={!isAdmin}
          />
        </div>

        <div className="pt-4 border-t border-slate-100" data-testid="call-summary-digest-row">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-[13.5px] font-semibold text-slate-800">Email me a daily call digest</div>
              <div className="text-[12px] text-slate-500 mt-0.5">One combined email covering all inbound and outbound calls for the day.</div>
            </div>
            <Toggle
              checked={settings.dailyDigestEnabled}
              onChange={(v) => setSettings((s) => ({ ...s, dailyDigestEnabled: v }))}
              testId="call-summary-digest-toggle"
              disabled={!isAdmin}
            />
          </div>
          {settings.dailyDigestEnabled && (
            <div className="mt-3 flex items-center gap-2">
              <Clock className="h-3.5 w-3.5 text-slate-400" />
              <span className="text-[12.5px] text-slate-600">Send at</span>
              <input
                type="time"
                value={settings.dailyDigestTime}
                onChange={(e) => setSettings((s) => ({ ...s, dailyDigestTime: e.target.value }))}
                data-testid="call-summary-digest-time-input"
                disabled={!isAdmin}
                className="h-9 px-2 rounded-md border border-slate-300 text-[13px] focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
              />
            </div>
          )}
        </div>

        {isAdmin && (
          <div className="flex justify-end pt-2">
            <Button onClick={save} disabled={saving} data-testid="call-summary-save-btn" className="h-10 bg-indigo-600 hover:bg-indigo-700 text-white text-[13px] font-semibold">
              {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
              {saving ? "Saving…" : "Save Settings"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
